import { useCallback, useEffect, useRef, useState, type ComponentType, type CSSProperties } from 'react';
import { EmptyState, Icon, showToast, Tooltip, Trans } from '@orgazone/ui-web';
import {
  CONNECTEURS, connecteurDe, couleursStatut, etatAVenir, teintesDe,
  type Connecteur, type EchangeConnecteur, type EtatConnecteur, type IdentifiantConnecteur,
} from '../integrations';
import { WalletCard } from './wallet-card';
import { walletConnection, type WalletConnection } from '../wallet-card-model';
import { useStoreScope } from '../store-context';
import { keyText, t } from '../i18n';

/**
 * INTÉGRATIONS — les services externes du magasin : leur état, leurs identifiants, leurs échanges.
 *
 * UTILITÉ : c'est l'écran qui répond à « est-ce que ça communique, et sinon pourquoi ». Il ne connaît
 * aucun service par son nom — il rend le REGISTRE (`../integrations`), et un connecteur de plus est
 * une entrée de plus dans ce registre, jamais une modification d'ici.
 *
 * ⚠️ CE QUI N'EST PAS CONSTRUIT (messagerie, e-commerce) EST ANNONCÉ « À VENIR ». C'est la réalité de
 * l'application, et l'écran la montre telle quelle plutôt
 * que de peupler la page de services plausibles (§23.0). La différence se voit : un connecteur à venir
 * n'a ni pastille verte, ni champ de clé à remplir, ni bouton de test — il expose ce qu'il FERA et ce
 * qui lui manque encore.
 *
 * ⚠️ UN BOUTON QUI NE PEUT RIEN FAIRE EST DÉSACTIVÉ ET DIT POURQUOI, il ne disparaît pas. La page doit
 * montrer la forme complète du poste de pilotage — c'est sa raison d'être — mais un contrôle actif qui
 * n'agirait pas serait l'interdit n°4 du §23.0. L'infobulle porte le motif, parce qu'un `title` HTML
 * n'est pas rendu sur un contrôle désactivé.
 *
 * ⚠️ « TESTER LA CONNEXION » N'EST PAS UN GESTE À PART : c'est le fait de rejouer la SONDE du
 * connecteur, celle-là même qui a rempli l'écran. Un test qui emprunterait un autre chemin que la
 * lecture normale ne prouverait rien de la lecture normale.
 */
export function Integrations() {
  const scope = useStoreScope();
  const gln = scope.view?.active?.gln ?? null;
  const [selection, setSelection] = useState<string | null>(null);
  const [lectures, setLectures] = useState<Record<string, ReadOnlyView>>(() => lecturesInitiales());
  // ⚠️ Chaque relecture d'une sonde fait avancer ce compteur, et les listes servies du connecteur se relisent
  //    avec lui : une clé posée ou un magasin changé relisait la connexion mais pas la liste des gabarits.
  const [probeGeneration, setProbeGeneration] = useState(0);

  /**
   * Interroge la sonde d'un connecteur et range ce qu'elle rapporte.
   *
   * ⚠️ L'ÉCHEC GARDE SON MESSAGE et n'est pas traduit en « déconnecté » : ne pas avoir PU lire l'état
   * d'un service et savoir qu'il est hors ligne sont deux faits différents, qui ne se corrigent pas au
   * même endroit. Les confondre ferait chercher une panne de service là où c'est notre API qui répond mal.
   */
  // ⚠️ SEULE LA DERNIÈRE SONDE D'UN CONNECTEUR ÉCRIT SON ÉTAT : un magasin changé ou une clé posée la relancent, et
  //    l'état de l'ancien magasin, revenu en dernier, s'afficherait sous le nouveau. Par connecteur : les sondes de
  //    deux connecteurs différents ne se dépassent pas.
  const probeAsked = useRef<Record<string, number>>({});
  const sonder = useCallback((c: Connecteur) => {
    if (!c.sonde) return;
    const mine = (probeAsked.current[c.key] ?? 0) + 1;
    probeAsked.current[c.key] = mine;
    setProbeGeneration((n) => n + 1);
    setLectures((l) => ({ ...l, [c.key]: { phase: 'chargement' } }));
    c.sonde()
      .then((etat) => {
        if (probeAsked.current[c.key] !== mine) return;
        setLectures((l) => ({ ...l, [c.key]: { phase: 'lu', etat } }));
      })
      .catch((e: unknown) => {
        if (probeAsked.current[c.key] !== mine) return;
        setLectures((l) => ({ ...l, [c.key]: { phase: 'echec', message: e instanceof Error ? e.message : String(e) } }));
      });
  }, []);

  // ⚠️ LES SONDES ATTENDENT LE MAGASIN ACTIF : la météo est cadrée sur le code postal du point de
  //    vente, et l'interroger sans magasin ne rapporterait rien d'utilisable. Sans magasin, la page
  //    reste lisible — le catalogue des intégrations ne dépend d'aucun magasin — mais les états, non.
  useEffect(() => {
    if (!gln) { setLectures(lecturesInitiales()); return; }
    for (const c of CONNECTEURS) if (c.sonde) sonder(c);
  }, [gln, sonder]);

  const current = connecteurDe(selection);
  if (current) {
    return (
      <Detail
        c={current}
        lecture={lectures[current.key] ?? { phase: 'lu', etat: etatAVenir() }}
        withoutStore={!gln && current.sonde !== null}
        reloadKey={`${gln ?? ''}:${probeGeneration}`}
        onRetour={() => setSelection(null)}
        onSonder={() => sonder(current)}
      />
    );
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 18, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 250 }}>
          <h1 style={{ margin: 0, fontSize: 'var(--t-h1)', fontWeight: 700, letterSpacing: '-0.025em', color: 'var(--ink-900)' }}>{t('Intégrations')}</h1>
          <p style={{ margin: '3px 0 0', fontSize: 'var(--t-title)', color: 'var(--ink-500)' }}>
            
            {t('Les services connectés au magasin — état, identifiants et journal des échanges.')}
          </p>
        </div>
      </div>

      {/* ⚠️ `auto-fill` ET NON `auto-fit` : avec cinq cartes le dernier rang en compte souvent une seule,
          et `auto-fit` l'étirerait sur toute la largeur — la carte isolée cesse alors de ressembler à
          ses voisines. Même correctif que les vignettes de caisse de l'écran Z & clôtures. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 13 }}>
        {CONNECTEURS.map((c) => (
          <Carte
            key={c.key}
            c={c}
            lecture={lectures[c.key] ?? { phase: 'lu', etat: etatAVenir() }}
            withoutStore={!gln && c.sonde !== null}
            onOpen={() => setSelection(c.key)}
          />
        ))}
      </div>

      <div style={{ marginTop: 18, padding: '13px 15px', borderRadius: 'var(--r-sm)', background: 'var(--ink-50)', border: '1px solid var(--ink-200)', fontSize: 'var(--t-body)', color: 'var(--ink-600)', lineHeight: 1.6, maxWidth: 760 }}>
        <Trans k='Un terminal <1>Mobile Partner</1> est du matériel du magasin, pas un service externe : la Console Orgazone le déclare, et il apparaît dans Magasin & caisses. Les imports <2>EDI</2> vivent dans Commandes & réceptions.' parts={[(x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>, (x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>]} /></div>
    </>
  );
}

// ---------------------------------------------------------------------------
// LECTURE D'ÉTAT
// ---------------------------------------------------------------------------

/**
 * Où en est la lecture de l'état d'un connecteur.
 *
 * ⚠️ « CHARGEMENT » EST UN ÉTAT À PART ENTIÈRE et non l'absence d'état : pendant l'interrogation, la
 * pastille doit dire « Lecture… » et surtout pas afficher le dernier statut connu ni un vert par
 * défaut. C'est le seul moment où l'écran ne sait rien, et il doit pouvoir le dire.
 */
type ReadOnlyView =
  | { phase: 'chargement' }
  | { phase: 'lu'; etat: EtatConnecteur }
  | { phase: 'echec'; message: string };

/** Au premier rendu : les connecteurs non construits ont déjà leur état, les autres restent à lire. */
function lecturesInitiales(): Record<string, ReadOnlyView> {
  const l: Record<string, ReadOnlyView> = {};
  for (const c of CONNECTEURS) if (!c.sonde) l[c.key] = { phase: 'lu', etat: etatAVenir() };
  return l;
}

/** L'état affichable d'un connecteur, quelle que soit la phase de sa lecture. */
function etatAffiche(lecture: ReadOnlyView, withoutStore: boolean): EtatConnecteur {
  if (withoutStore) {
    return { ...etatAVenir(), statut: 'attention', displayLabel: t('Magasin non sélectionné'), meta: t('L’état se lit sur un magasin actif') };
  }
  if (lecture.phase === 'lu') return lecture.etat;
  if (lecture.phase === 'chargement') {
    return { ...etatAVenir(), statut: 'lecture', displayLabel: t('Lecture…'), meta: t('Interrogation du service en cours') };
  }
  return {
    ...etatAVenir(),
    statut: 'erreur',
    displayLabel: t('État illisible'),
    meta: lecture.message,
    alerte: {
      heading: t('L’état de ce service n’a pas pu être lu'),
      message: lecture.message,
      remede: t('L’échec vient de notre API, pas forcément du service : le connecteur peut très bien fonctionner. Réessayer, puis vérifier les journaux du back-office.'),
    },
  };
}

// ---------------------------------------------------------------------------
// VUE LISTE
// ---------------------------------------------------------------------------

/** Une intégration dans la grille : identité, état, et de quoi entrer dans son détail. */
function Carte({ c, lecture, withoutStore, onOpen }: { c: Connecteur; lecture: ReadOnlyView; withoutStore: boolean; onOpen: () => void }) {
  const etat = etatAffiche(lecture, withoutStore);
  const st = couleursStatut(etat.statut);
  return (
    <button
      type="button"
      onClick={onOpen}
      className="oz-integ-card"
      style={{
        display: 'flex', flexDirection: 'column', gap: 12, padding: '17px 18px',
        border: `1px solid ${st.bd}`, borderRadius: 'var(--r-md)', background: 'var(--paper)',
        textAlign: 'left', cursor: 'pointer', font: 'inherit', color: 'inherit',
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 11, width: '100%' }}>
        <Initiales c={c} taille={38} police={10} />
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 'var(--t-strong)', fontWeight: 700, color: 'var(--ink-900)' }}>{t(c.nom)}</span>
          <span style={{ display: 'block', fontSize: 'var(--t-dense)', color: 'var(--ink-500)', marginTop: 1 }}>{t(c.description)}</span>
        </span>
        <span style={{ color: 'var(--ink-300)', display: 'flex', flexShrink: 0 }}><Icon name="chevronR" size={15} stroke={2.2} /></span>
      </span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', flexWrap: 'wrap' }}>
        <PastilleEtat etat={etat} hauteur={24} />
        <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-400)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{etat.meta}</span>
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// VUE DÉTAIL
// ---------------------------------------------------------------------------

/** Le poste de pilotage d'une intégration : connexion, suivi, échanges. */
function Detail({ c, lecture, withoutStore, reloadKey, onRetour, onSonder }: {
  c: Connecteur; lecture: ReadOnlyView; withoutStore: boolean; reloadKey: string; onRetour: () => void; onSonder: () => void;
}) {
  const etat = etatAffiche(lecture, withoutStore);
  const enLecture = lecture.phase === 'chargement';

  /**
   * Rejoue la sonde et rend son verdict à l'écran.
   *
   * ⚠️ LE RÉSULTAT VIENT DE LA LECTURE ELLE-MÊME, pas d'un message écrit d'avance : c'est le statut
   * rapporté qui décide du ton du bandeau. Un « connexion vérifiée » affiché quoi qu'il arrive serait
   * exactement l'échec avalé du §23.0.
   */
  const tester = () => {
    if (!c.sonde) return;
    onSonder();
    c.sonde()
      .then((e) => showToast(
        e.statut === 'connecte' ? t('{nom} — service joignable', { nom: t(c.nom) }) : `${t(c.nom)} — ${e.displayLabel}${e.alerte ? ` : ${e.alerte.message}` : ''}`,
        e.statut === 'connecte' ? 'info' : 'warn', 6000,
      ))
      .catch((e: unknown) => showToast(t('Test impossible : {message}', { message: e instanceof Error ? e.message : String(e) }), 'err', 7000));
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 13, marginBottom: 18, flexWrap: 'wrap' }}>
        <button
          type="button" onClick={onRetour} aria-label={t('Retour aux intégrations')} className="oz-integ-btn"
          style={{ width: 36, height: 36, border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-700)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}
        >
          <Icon name="arrowL" size={16} stroke={2.2} />
        </button>
        <Initiales c={c} taille={40} police={10.5} />
        <div style={{ flex: 1, minWidth: 200 }}>
          <h1 style={{ margin: 0, fontSize: 'var(--t-feature)', fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--ink-900)' }}>{t(c.nom)}</h1>
          <p style={{ margin: '2px 0 0', fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>{t(c.description)}</p>
        </div>
        <PastilleEtat etat={etat} hauteur={26} />
      </div>

      {etat.alerte ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '12px 15px', borderRadius: 'var(--r-sm)', background: 'var(--rose-bg)', border: '1px solid var(--rose)', marginBottom: 16, flexWrap: 'wrap' }}>
          <span style={{ flex: 1, minWidth: 230, fontSize: 'var(--t-body)', color: 'var(--rose-fg)', lineHeight: 1.5 }}>
            <strong>{etat.alerte.heading}</strong> — {etat.alerte.message}{' '}
            <span style={{ opacity: 0.9 }}>{etat.alerte.remede}</span>
          </span>
          {/* ⚠️ « RÉESSAYER LA LECTURE » ET NON « RELANCER LA SYNCHRONISATION » (libellé de la maquette) :
              ce connecteur LIT, il ne synchronise rien. Le dessin du bouton est celui de la maquette, son
              libellé est celui du geste réel — un libellé qui nomme une opération inexistante est
              l'interdit n°2 du §23.0. */}
          <BoutonAction
            displayLabel={enLecture ? t('Lecture en cours…') : t('Réessayer la lecture')}
            onClick={tester}
            actif={c.sonde !== null && !withoutStore && !enLecture}
            reason={motifIndisponible(c, withoutStore)}
            plein
          />
        </div>
      ) : null}

      {/* ⚠️ `auto-fit` ICI (et `auto-fill` sur la LISTE, plus haut) — ce n'est pas une incohérence,
          les deux situations sont inverses. Le détail n'a que DEUX cartes : avec `auto-fill`, un
          écran large fabrique des colonnes VIDES et les deux cartes se tassent à gauche sur un tiers
          de la largeur. `auto-fit` les fait remplir, comme la maquette. La liste, elle, compte cinq
          cartes dont le dernier rang en porte souvent une seule, qu'`auto-fit` étirerait sur toute
          la largeur — d'où le choix opposé. Relevé sur la maquette, pas déduit. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 14, marginBottom: 16 }}>
        <CarteConnexion
          c={c} etat={etat} withoutStore={withoutStore} enLecture={enLecture} onTester={tester}
          onPose={() => { onSonder(); tester(); }}
        />
        <CarteSuivi c={c} etat={etat} />
      </div>

      {/* ⚠️ LES RÉGLAGES D'UN CONNECTEUR VIVENT DANS SON DÉTAIL, pas dans Paramètres — et seulement quand
          le connecteur EXISTE : régler un service non construit serait une option non branchée (§23.0).
          Déclarés au registre, rendus par LA CARTE du connecteur (`SETTINGS_CARDS`). */}
      {c.sonde && c.reglages && c.reglages.length > 0 ? (() => {
        const Card = SETTINGS_CARDS[c.key];
        return Card ? (
          <Card withoutStore={withoutStore} reloadKey={reloadKey}
            connection={walletConnection(lecture.phase, lecture.phase === 'lu' ? lecture.etat.statut : undefined)} />
        ) : null;
      })() : null}

      <Journal c={c} etat={etat} />
    </>
  );
}

/** La carte « Connexion » : quel secret est en place, et ce qu'on peut en faire. */
function CarteConnexion({ c, etat, withoutStore, enLecture, onTester, onPose }: {
  c: Connecteur; etat: EtatConnecteur; withoutStore: boolean; enLecture: boolean;
  onTester: () => void;
  /** Rejoue la lecture après une pose réussie — l'état affiché doit refléter la clé qui vient d'entrer. */
  onPose: () => void;
}) {
  const [devoile, setDevoile] = useState(false);
  // ⚠️ SAISIE EN LIGNE, PAS EN MODALE : c'est l'idiome de cette application (cf. la clé Pennylane dans
  //    Export comptable). Une modale aurait demandé un vocabulaire de classes que le back-office n'a
  //    pas — l'audit des classes CSS l'a refusée, à raison.
  const [saisie, setSaisie] = useState(false);
  const [cle, setCle] = useState('');
  const [occupe, setOccupe] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  /**
   * Pose la clé. ⚠️ CE QUI REVIENT EST UN VERDICT, PAS UN ACCUSÉ DE RÉCEPTION : le serveur ÉPROUVE la
   * clé chez le fournisseur avant de la sceller. Refusée, rien n'est enregistré et c'est SON motif qui
   * s'affiche — et la saisie reste ouverte, la clé dans le champ : la refermer obligerait à retaper.
   */
  const poser = async () => {
    if (!c.poseCle || !cle.trim() || occupe) return;
    setOccupe(true); setFailure(null);
    try {
      const r = await c.poseCle(cle.trim());
      if (!r.ok) { setFailure(r.detail); return; }
      showToast(r.detail, 'ok', 6000);
      setCle(''); setSaisie(false);
      onPose();
    } catch (e) {
      setFailure(e instanceof Error ? e.message : String(e));
    } finally { setOccupe(false); }
  };
  const revelable = etat.identifiant.kind === 'masque' && etat.identifiant.complet !== undefined;
  const reason = motifIndisponible(c, withoutStore);

  return (
    <div className="dcard" style={{ overflow: 'hidden' }}>
      <EnteteCarte heading={t('Connexion')} />
      <div style={{ padding: '14px 17px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink-500)', marginBottom: 5 }}>{t(c.libelleCle)}</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ flex: 1, minWidth: 0, minHeight: 36, display: 'flex', alignItems: 'center', padding: '0 12px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--ink-50)', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-body)', color: 'var(--ink-800)', lineHeight: 1.45, overflow: 'hidden' }}>
              {texteIdentifiant(etat.identifiant, devoile)}
            </span>
            {/* ⚠️ L'ŒIL N'APPARAÎT QUE S'IL Y A QUELQUE CHOSE À DÉVOILER : proposer de révéler une clé
                que le serveur ne transmet pas (ou qui n'existe pas) serait offrir une option morte.
                ⚠️ ET IL NE RÉVÈLE QUE LA FIN — quatre caractères, de quoi reconnaître LAQUELLE des
                clés est en place sans jamais faire sortir la clé entière du serveur. Le libellé le
                dit, pour ne pas promettre un dévoilement complet qui n'aura pas lieu. */}
            {revelable ? (
              <button
                type="button" onClick={() => setDevoile((v) => !v)} className="oz-integ-btn"
                aria-label={devoile ? t('Masquer la fin de la clé') : t('Afficher la fin de la clé')}
                style={{ width: 36, height: 36, border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-600)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, cursor: 'pointer' }}
              >
                <Icon name="eye" size={15} stroke={2} />
              </button>
            ) : null}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <BoutonAction
            displayLabel={enLecture ? t('Test en cours…') : t('Tester la connexion')}
            onClick={onTester}
            actif={c.sonde !== null && !withoutStore && !enLecture}
            reason={reason}
          />
          {/* ⚠️ LA SAISIE EST DÉCLARÉE, PAS CODÉE ICI : le bouton s'active pour tout connecteur qui
              propose un `poseCle`. Ajouter un connecteur saisissable reste une ligne de catalogue —
              même règle que la sonde. Sans `poseCle`, le motif dit POURQUOI on ne peut pas saisir,
              et les deux raisons ne se confondent pas (pas encore construit / réglage de serveur). */}
          <BoutonAction
            displayLabel={saisie ? t('Annuler la saisie') : t('Modifier les identifiants')}
            onClick={() => { setSaisie((v) => !v); setFailure(null); }}
            actif={Boolean(c.poseCle) && !withoutStore}
            reason={
              c.poseCle
                ? undefined
                : c.identifiantsModifiables
                  ? t('La saisie d’identifiants depuis l’écran n’est pas encore construite.')
                  : t('Ce secret est un réglage de DÉPLOIEMENT : il se change dans l’environnement du serveur, pas depuis le navigateur.')
            }
          />
        </div>

        {saisie && c.poseCle ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '12px 13px', borderRadius: 'var(--r-sm)', background: 'var(--ink-50)', border: '1px solid var(--ink-200)' }}>
            <label htmlFor="integ-cle" style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>
              {t(c.libelleCle)}
            </label>
            <input
              id="integ-cle" type="password" autoComplete="new-password" autoFocus
              value={cle} onChange={(e) => setCle(e.target.value)}
              placeholder={etat.identifiant.kind === 'absent' ? t('à renseigner') : t('nouvelle clé — l’actuelle sera remplacée')}
              style={{ height: 36, padding: '0 12px', border: '1px solid var(--ink-300)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-900)', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-body)' }}
            />
            {/* Ce que l'opérateur doit savoir AVANT de valider, pas après. */}
            <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)', lineHeight: 1.5 }}>
              <Trans k='La clé est <1>éprouvée chez le fournisseur</1> avant d’être scellée : refusée, rien n’est enregistré et son motif s’affiche ici. Elle ne redescend jamais ensuite — elle ne peut être que remplacée.' parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} /></div>
            {failure ? (
              <div style={{ fontSize: 'var(--t-dense)', color: 'var(--rose-fg)', lineHeight: 1.5 }}>{failure}</div>
            ) : null}
            <div>
              <BoutonAction
                displayLabel={occupe ? t('Vérification…') : t('Éprouver et enregistrer')}
                onClick={() => { void poser(); }}
                actif={!occupe && cle.trim().length > 0}
                plein
              />
            </div>
          </div>
        ) : null}

        <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-400)', lineHeight: 1.5 }}>{t(c.noteConnexion)}</div>
      </div>
    </div>
  );
}

/**
 * La seconde carte : ce que le connecteur SUIT quand il tourne, ce qu'il FERA quand il n'existe pas.
 *
 * ⚠️ DEUX CONTENUS, UNE SEULE CARTE, et le critère est mécanique : y a-t-il des indicateurs à montrer ?
 * Dessiner « Dernier envoi : — » pour un service qui n'a jamais rien envoyé donnerait à voir le
 * squelette d'une mécanique qui n'existe pas — la forme dirait « branché », les valeurs diraient le
 * contraire. Mieux vaut dire franchement ce qui est prévu.
 */
function CarteSuivi({ c, etat }: { c: Connecteur; etat: EtatConnecteur }) {
  if (etat.indicateurs.length > 0) {
    return (
      <div className="dcard" style={{ overflow: 'hidden' }}>
        <EnteteCarte heading={t('Synchronisation')} />
        {etat.indicateurs.map((i) => (
          <div key={i.displayLabel} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '0 17px', minHeight: 44, borderBottom: '1px solid var(--ink-100)' }}>
            <span style={{ flex: 1, fontSize: 'var(--t-body)', color: 'var(--ink-600)' }}>{i.displayLabel}</span>
            <span className="tnum" style={{ fontSize: 'var(--t-body)', fontWeight: 700, color: i.alerte ? 'var(--rose-fg)' : 'var(--ink-900)', textAlign: 'right' }}>{i.valeur}</span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="dcard" style={{ overflow: 'hidden' }}>
      <EnteteCarte heading={t('Ce que fera cette intégration')} />
      <div style={{ padding: '14px 17px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 9 }}>
          {c.capacites.map((cap) => (
            <li key={keyText(cap)} style={{ display: 'flex', gap: 9, fontSize: 'var(--t-body)', color: 'var(--ink-700)', lineHeight: 1.55 }}>
              <span style={{ color: 'var(--ink-300)', flexShrink: 0, display: 'flex', paddingTop: 3 }}><Icon name="dot" size={12} stroke={2} /></span>
              {t(cap)}
            </li>
          ))}
        </ul>
        {c.manque && c.manque.length > 0 ? (
          <div style={{ padding: '11px 13px', borderRadius: 'var(--r-sm)', background: 'var(--ink-50)', border: '1px solid var(--ink-200)' }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink-500)', marginBottom: 7 }}>{t('Ce qui manque')}</div>
            <ul style={{ margin: 0, paddingLeft: 16, display: 'flex', flexDirection: 'column', gap: 7 }}>
              {c.manque.map((m) => (
                <li key={keyText(m)} style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-600)', lineHeight: 1.55 }}>{t(m)}</li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** Ce qu'une carte de réglages de connecteur reçoit du détail. */
export interface SettingsCardProps {
  withoutStore: boolean;
  /** Change quand la connexion se relit — les listes servies par le fournisseur se relisent avec elle. */
  reloadKey: string;
  connection: WalletConnection;
}

/**
 * LA CARTE DE RÉGLAGES DE CHAQUE CONNECTEUR QUI EN DÉCLARE — par clé de registre.
 *
 * ⚠️ 🔴 UN CONNECTEUR QUI DÉCLARE DES RÉGLAGES SANS CARTE LES REND INVISIBLES PARTOUT : Paramètres les
 * écarte de ses onglets parce qu'ils vivent ici (`REGLES_AILLEURS`). `integrations-registre.test.ts`
 * exige donc une carte pour chaque déclaration.
 * ⚠️ Une carte PAR connecteur, et non une ligne générique : le dessin du wallet (interrupteurs, un
 * gabarit par type de titre, « déjà pris ») n'a rien d'une liste de réglages quelconque.
 */
export const SETTINGS_CARDS: Readonly<Record<string, ComponentType<SettingsCardProps>>> = {
  wallet: WalletCard,
};

/**
 * Le journal des échanges — ce qui est parti, ce qui est revenu, et le contenu exact.
 *
 * ⚠️ AUCUN ÉCHANGE N'EST CONSIGNÉ AUJOURD'HUI, et l'état vide le dit sans détour. La tentation serait
 * de lister les lectures faites par cet onglet depuis son ouverture : ce serait vrai mais trompeur —
 * un journal qui repart de zéro à chaque rafraîchissement laisse croire qu'un service n'a échangé
 * qu'une fois. Un journal est une CONSERVATION, et elle reste à construire côté serveur.
 */
function Journal({ c, etat }: { c: Connecteur; etat: EtatConnecteur }) {
  const [deplie, setDeplie] = useState(-1);
  return (
    <div className="dcard" style={{ overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 17px', borderBottom: '1px solid var(--ink-200)', flexWrap: 'wrap' }}>
        <span style={{ flex: 1, minWidth: 170, fontSize: 'var(--t-control)', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Journal des échanges')}</span>
        {etat.exchanges.length > 0 ? (
          <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-400)' }}>{t('Cliquez une ligne pour consulter le contenu échangé')}</span>
        ) : null}
      </div>

      {etat.exchanges.length === 0 ? (
        <EmptyState
          icon="history"
          title={t('Aucun échange consigné')}
          note={
            c.sonde
              ? t('Les appels vers {nom} ne sont pas encore archivés : le back-office ne tient pas de journal d’échanges. C’est une capacité serveur à construire, pas une absence d’activité.', { nom: t(c.nom) })
              : t('{nom} n’échange rien : son connecteur n’est pas construit.', { nom: t(c.nom) })
          }
        />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: 850 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '150px 64px 1fr 190px 100px 72px', padding: '8px 17px', borderBottom: '1px solid var(--ink-100)', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>
              <span>{t('Horodatage')}</span><span>{t('Sens')}</span><span>{t('Opération')}</span><span>{t('Client')}</span><span>{t('Statut')}</span><span style={{ textAlign: 'right' }}>{t('Durée')}</span>
            </div>
            {etat.exchanges.map((e, i) => (
              <LigneEchange key={`${e.horodatage}-${i}`} e={e} ouvert={deplie === i} onBasculer={() => setDeplie(deplie === i ? -1 : i)} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** Une ligne du journal, et son contenu déplié. */
function LigneEchange({ e, ouvert, onBasculer }: { e: EchangeConnecteur; ouvert: boolean; onBasculer: () => void }) {
  return (
    <div>
      <button
        type="button" onClick={onBasculer} className="oz-integ-ligne"
        style={{ display: 'grid', gridTemplateColumns: '150px 64px 1fr 190px 100px 72px', width: '100%', padding: '10px 17px', border: 'none', borderBottom: '1px solid var(--ink-100)', background: ouvert ? 'var(--ink-50)' : 'transparent', textAlign: 'left', alignItems: 'center', cursor: 'pointer', font: 'inherit' }}
      >
        <span className="tnum" style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-note)', color: 'var(--ink-600)' }}>{e.horodatage}</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 'var(--t-meta)', fontWeight: 700, color: e.sens === 'sortant' ? 'var(--teal-700)' : 'var(--blue-fg)' }}>
          <Icon name={e.sens === 'sortant' ? 'outbound' : 'inbound'} size={13} stroke={2.2} />
          {e.sens === 'sortant' ? t('Sortant') : t('Entrant')}
        </span>
        <span style={{ fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', paddingRight: 10 }}>{e.operation}</span>
        {/* ⚠️ « — » QUAND L'ÉCHANGE NE DÉSIGNE PERSONNE, et c'est une information : une création de
            carte ou une lecture de réglages n'a pas de client, et afficher un nom voisin serait pire
            que le tiret. Un appel qui EN désigne un sans qu'on le retrouve rend le même tiret : la
            fiche a pu être effacée depuis. */}
        <span style={{ paddingRight: 10, minWidth: 0 }}>
          {e.client ? (
            <>
              <span style={{ display: 'block', fontSize: 'var(--t-dense)', fontWeight: 600, color: 'var(--ink-800)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.client.name}</span>
              {!!e.client.card && (
                <span className="tnum" style={{ display: 'block', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-sub)', color: 'var(--ink-400)' }}>{e.client.card}</span>
              )}
            </>
          ) : (
            <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-300)' }}>—</span>
          )}
        </span>
        <span>
          <span style={{ height: 20, display: 'inline-flex', alignItems: 'center', padding: '0 8px', borderRadius: 'var(--r-pill)', background: e.ok ? 'var(--teal-100)' : 'var(--rose-bg)', color: e.ok ? 'var(--teal-800)' : 'var(--rose-fg)', fontSize: 'var(--t-sub)', fontWeight: 700 }}>{e.statut}</span>
        </span>
        <span className="tnum" style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-note)', color: 'var(--ink-400)' }}>{e.duree}</span>
      </button>
      {ouvert ? (
        <div style={{ padding: '13px 17px', background: 'var(--ink-950)', borderBottom: '1px solid var(--ink-100)' }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', letterSpacing: '.07em', textTransform: 'uppercase', color: 'rgba(255,255,255,.45)', marginBottom: 7 }}>{t('Contenu échangé')}</div>
          <pre style={{ margin: 0, fontFamily: 'var(--font-mono)', fontSize: 'var(--t-note)', lineHeight: 1.6, color: '#d4f7ec', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{e.contenu}</pre>
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PIÈCES COMMUNES
// ---------------------------------------------------------------------------

/** En-tête d'une carte de détail. */
function EnteteCarte({ heading }: { heading: string }) {
  return (
    <div style={{ padding: '14px 17px', borderBottom: '1px solid var(--ink-200)', fontSize: 'var(--t-control)', fontWeight: 700, color: 'var(--ink-900)' }}>{heading}</div>
  );
}

/** La pastille d'initiales — le repère visuel d'un service. ⚠️ Du texte mono, jamais une icône. */
function Initiales({ c, taille, police }: { c: Connecteur; taille: number; police: number }) {
  const teinte = teintesDe(c.teinte);
  return (
    <span style={{ width: taille, height: taille, borderRadius: 'var(--r-sm)', background: teinte.bg, color: teinte.fg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontFamily: 'var(--font-mono)', fontSize: police, fontWeight: 800, letterSpacing: '.04em' }}>
      {c.initiales}
    </span>
  );
}

/** La pastille d'état : un point, un mot. */
function PastilleEtat({ etat, hauteur }: { etat: EtatConnecteur; hauteur: number }) {
  const st = couleursStatut(etat.statut);
  return (
    <span style={{ height: hauteur, display: 'inline-flex', alignItems: 'center', gap: 6, padding: hauteur >= 26 ? '0 10px' : '0 9px', borderRadius: 'var(--r-pill)', background: st.bg, color: st.fg, fontSize: hauteur >= 26 ? 12 : 11.5, fontWeight: 700, flexShrink: 0 }}>
      <span style={{ width: 7, height: 7, borderRadius: 'var(--r-pill)', background: st.dot }} />
      {etat.displayLabel}
    </span>
  );
}

/**
 * Un bouton d'action qui, quand il ne peut rien faire, le DIT au lieu de disparaître.
 *
 * ⚠️ DÉSACTIVÉ PLUTÔT QU'ABSENT : cette page décrit un poste de pilotage, et masquer les commandes
 * indisponibles laisserait croire qu'elles n'existeront jamais. ⚠️ MAIS JAMAIS ACTIF SANS EFFET : un
 * bouton qui répondrait par un message d'attente serait l'interdit n°4 du §23.0. L'infobulle porte le
 * motif — un `title` HTML n'est pas rendu sur un contrôle désactivé, d'où la primitive partagée.
 */
function BoutonAction({ displayLabel, onClick, actif, reason, plein }: {
  // ⚠️ `reason` OPTIONNEL : un bouton actif n'a aucun motif d'indisponibilité à porter, et en exiger
  //    un pousserait à inventer une phrase que personne ne lira.
  displayLabel: string; onClick: () => void; actif: boolean; reason?: string; plein?: boolean;
}) {
  const base: CSSProperties = plein
    ? { height: 32, padding: '0 13px', border: 'none', borderRadius: 'var(--r-xs)', background: 'var(--rose-fg)', color: '#fff', fontSize: 'var(--t-body)', fontWeight: 700, whiteSpace: 'nowrap' }
    // ⚠️ 34 px, relevé sur la maquette — pas 36. Deux pixels sur un bouton isolé ne se voient pas ;
    //    côte à côte avec les autres commandes de l'écran, le décalage se voit.
    : { height: 34, padding: '0 14px', border: '1px solid var(--ink-300)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-800)', fontSize: 'var(--t-body)', fontWeight: 600 };

  const bouton = (
    <button
      type="button" onClick={actif ? onClick : undefined} disabled={!actif} className={actif ? 'oz-integ-btn' : undefined}
      // ⚠️ Pas de `cursor` quand le bouton est désactivé : l'enveloppe d'infobulle le porte à sa place
      //    (l'enfant désactivé y devient transparent au pointeur, sans quoi la bulle ne s'ouvrirait jamais).
      style={{ ...base, ...(actif ? { cursor: 'pointer' } : null), opacity: actif ? 1 : 0.55 }}
    >
      {displayLabel}
    </button>
  );
  return actif ? bouton : <Tooltip label={t('Indisponible')} content={reason}>{bouton}</Tooltip>;
}

/** Pourquoi une action est hors d'atteinte sur ce connecteur — le motif exact, jamais « indisponible ». */
function motifIndisponible(c: Connecteur, withoutStore: boolean): string {
  if (!c.sonde) return t('Le connecteur {nom} n’est pas construit : il n’y a rien à interroger.', { nom: t(c.nom) });
  if (withoutStore) return t('L’état se lit sur un magasin actif — choisissez d’abord le magasin à administrer.');
  return t('Lecture déjà en cours.');
}

/** Le texte du champ d'identifiant — ou ce qui tient sa place quand il n'y a pas de clé à montrer. */
function texteIdentifiant(id: IdentifiantConnecteur, devoile: boolean): string {
  if (id.kind === 'masque') return devoile ? (id.complet ?? id.masque) : id.masque;
  if (id.kind === 'serveur') return id.ou;
  return t('Aucun identifiant enregistré');
}
